/**
 * Client-side fingerprint collector.
 *
 * Collects hardware-only signals for server-side fingerprint hashing.
 * Does NOT compute the hash client-side — that's done server-side to
 * prevent tampering. The raw signals are sent to POST /widget/identify.
 *
 * Signals collected:
 *   - FingerprintJS: canvas, audio, WebGL, screen, platform, timezone
 *   - Extra: hardwareConcurrency, deviceMemory, maxTouchPoints, unmasked GPU
 *   - Cookies: reads igreet_visitor_id + any identity cookies
 */

/**
 * Collect hardware fingerprint signals.
 * Uses native browser APIs — no FingerprintJS library dependency.
 *
 * @returns {Object} Raw fingerprint signals
 */
export async function collectFingerprint() {
    const fp = {};

    // ── Canvas signals ──────────────────────────────
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 50;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Geometry render
            ctx.fillStyle = '#f60';
            ctx.fillRect(10, 10, 80, 30);
            ctx.fillStyle = '#069';
            ctx.font = '14px Arial';
            ctx.fillText('iGreet FP', 2, 28);
            ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
            ctx.fillRect(30, 5, 60, 20);
            fp.canvas = {
                geometry: canvas.toDataURL(),
                text: ctx.measureText('iGreet FP').width.toString(),
            };
        }
    } catch { /* canvas blocked */ }

    // ── Audio signal ────────────────────────────────
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const analyser = audioCtx.createAnalyser();
        const gainNode = audioCtx.createGain();
        const scriptProcessor = audioCtx.createScriptProcessor(4096, 1, 1);

        gainNode.gain.value = 0; // silent
        oscillator.type = 'triangle';
        oscillator.frequency.value = 10000;

        oscillator.connect(analyser);
        analyser.connect(scriptProcessor);
        scriptProcessor.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        oscillator.start(0);
        const data = new Float32Array(analyser.frequencyBinCount);

        await new Promise(r => setTimeout(r, 100));
        analyser.getFloatFrequencyData(data);

        fp.audio = data.slice(0, 30).reduce((a, b) => a + b, 0).toString();

        oscillator.stop();
        audioCtx.close();
    } catch { /* audio blocked */ }

    // ── WebGL signals ───────────────────────────────
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (gl) {
            fp.webGlBasics = {
                renderer: gl.getParameter(gl.RENDERER),
            };
            // Unmasked GPU driver string (high entropy)
            const ext = gl.getExtension('WEBGL_debug_renderer_info');
            if (ext) {
                fp.webgl_unmasked_renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
            }
        }
    } catch { /* webgl blocked */ }

    // ── System signals ──────────────────────────────
    fp.screenResolution = `${screen.width}x${screen.height}x${screen.colorDepth}`;
    fp.platform = navigator.platform || '';
    fp.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

    // ── Extra hardware signals ──────────────────────
    fp.hardwareConcurrency = navigator.hardwareConcurrency || null;
    fp.deviceMemory = navigator.deviceMemory || null;
    fp.maxTouchPoints = navigator.maxTouchPoints || 0;

    return fp;
}

/**
 * Read cookies and build a snapshot for identification.
 * @returns {Object} Cookie snapshot with key-value pairs
 */
export function buildCookieSnapshot() {
    const cookies = {};
    if (typeof document === 'undefined') return cookies;

    document.cookie.split(';').forEach(c => {
        const [key, ...valParts] = c.trim().split('=');
        if (key) cookies[key.trim()] = valParts.join('=');
    });

    return {
        cookie_keys: Object.keys(cookies),
        igreet_visitor_id: cookies.igreet_visitor_id || null,
        ...cookies,
    };
}
